import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/* ============================================================
   O pedido de estimativa que veio da tela de publicar

   A tela de publicar decide (o interruptor), a tela do prato executa. O
   recado entre as duas é o `state` da navegação, e este hook é quem o
   lê.

   POR QUE QUEM CHAMA É A TELA DO PRATO. Ela é a única que tem onde
   mostrar "olhando a foto…" e onde pôr o erro. Se o `Compose`
   disparasse antes de navegar, o prato abriria com o botão "Estimar
   calorias" à mostra e uma chamada já a caminho — e um toque nele
   gastaria a SEGUNDA estimativa do dia da pessoa pra receber a mesma
   resposta.

   E é por isso que este arquivo existe separado, sem importar Firebase
   nenhum: gastar cota duas vezes é o erro caro aqui, e a trava contra
   isso precisava ser testável fora da tela.
   ============================================================ */

/** A marca que `Compose` põe na navegação ao publicar. */
export interface PedidoDeEstimativa { estimarJa?: boolean }

/**
 * Dispara UMA vez a estimativa pedida ao publicar.
 *
 * `pode` é o que a tela sabe e este hook não: que o prato é meu, que ele
 * ainda não tem caloria e que tem foto. Enquanto for falso, o pedido
 * fica guardado — o prato chega do Firestore um instante depois da tela,
 * e desistir nesse instante seria desistir sempre.
 *
 * Duas travas, e as duas são contra a mesma coisa (pedir de novo o que
 * já foi pedido, gastando a cota de alguém):
 *
 * - a marca é CONSUMIDA da navegação, então recarregar a tela não repete
 *   o pedido;
 * - e um `ref` segura o caso de a marca ainda estar lá quando o efeito
 *   roda de novo, antes de o React Router trocar a localização.
 */
export function usePedidoDeEstimativa(pode: boolean, pedir: () => void): void {
  const location = useLocation();
  const navigate = useNavigate();

  /* A função de pedir fica num `ref` pra não entrar nas dependências do
     efeito: ela é recriada a cada render da tela, e um efeito que
     dependesse dela rodaria de novo a cada render — que é exatamente o
     laço que este hook existe pra não ter. */
  const pedirAgora = useRef(pedir);
  pedirAgora.current = pedir;

  const jaPedi = useRef(false);
  const pediram = (location.state as PedidoDeEstimativa | null)?.estimarJa === true;

  useEffect(() => {
    if (!pediram || jaPedi.current || !pode) return;
    jaPedi.current = true;
    navigate(location.pathname + location.search, { replace: true, state: null });
    pedirAgora.current();
  }, [pediram, pode, navigate, location.pathname, location.search]);
}
